<script setup>
import {computed, ref, watch} from 'vue'
import {useRoute} from 'vue-router'
import {useHead} from '@vueuse/head'
import axios from 'axios'
import HomeHeader from '@/components/HomeHeader.vue'
import Footer from '@/components/Footer.vue'
import MarketingInquiryForm from '@/components/MarketingInquiryForm.vue'
import {useI18n} from '@/i18n'
import {applyImageFallback, excerptFromHtml, itemsFrom} from '@/utils/detailContent'

const route = useRoute()
const {t} = useI18n()
const detail = ref(null)
const relatedProjects = ref([])
const loading = ref(true)
const error = ref(false)
const pageContext = computed(() => `${t('page.project')}: ${detail.value?.project_title || route.params.str}`)

async function loadProject(slug) {
  loading.value = true
  error.value = false
  relatedProjects.value = []
  try {
    const detailResponse = await axios.get('/api/projects/', {params: {site: slug}})
    detail.value = itemsFrom(detailResponse)[0] || null
    if (!detail.value) {
      error.value = true
      return
    }
    loading.value = false

    try {
      const relatedResponse = await axios.get('/api/projects/')
      relatedProjects.value = itemsFrom(relatedResponse).filter((item) => item.id !== detail.value.id).slice(0, 3)
    } catch (relatedError) {
      relatedProjects.value = []
    }
  } catch (requestError) {
    error.value = true
  } finally {
    loading.value = false
  }
}

watch(() => route.params.str, (slug) => loadProject(slug), {immediate: true})

useHead({
  title: () => detail.value?.project_title || t('page.project'),
  meta: [{name: 'description', content: () => excerptFromHtml(detail.value?.content, 170)}]
})
</script>

<template>
  <div class="page mkt-page mkt-detail-page">
    <HomeHeader/>
    <main>
      <section v-if="loading" class="mkt-detail-state">
        <i class="mdi mdi-loading mdi-spin" aria-hidden="true"></i><p>{{ t('marketing.detail.common.loading') }}</p>
      </section>
      <section v-else-if="error || !detail" class="mkt-detail-state is-error">
        <i class="mdi mdi-alert-circle-outline" aria-hidden="true"></i><h1>{{ t('marketing.detail.common.notFound') }}</h1>
        <a class="mkt-button mkt-button-primary" href="/projects">{{ t('page.projects') }}</a>
      </section>

      <template v-else>
        <section class="mkt-case-detail-hero">
          <img class="mkt-case-detail-image" :src="detail.image_1" :alt="detail.project_title" @error="applyImageFallback">
          <div class="mkt-case-detail-shade"></div>
          <div class="mkt-shell mkt-case-detail-inner">
            <nav class="mkt-detail-breadcrumb is-light" aria-label="Breadcrumb">
              <a href="/">{{ t('common.home') }}</a><i class="mdi mdi-chevron-right"></i>
              <a href="/projects">{{ t('page.projects') }}</a><i class="mdi mdi-chevron-right"></i>
              <span>{{ detail.project_title }}</span>
            </nav>
            <div>
              <span class="mkt-eyebrow is-light">{{ t('marketing.detail.project.eyebrow') }}</span>
              <h1>{{ detail.project_title }}</h1>
              <p>{{ t('marketing.detail.project.lead') }}</p>
              <div class="mkt-detail-actions">
                <a class="mkt-button mkt-button-accent" href="#detail-inquiry">{{ t('marketing.detail.common.quote') }}<i class="mdi mdi-arrow-right"></i></a>
                <a class="mkt-button mkt-button-outline-light" href="/projects">{{ t('page.projects') }}</a>
              </div>
            </div>
          </div>
        </section>

        <section class="mkt-detail-metrics-band">
          <div class="mkt-shell">
            <article v-for="metric in t('marketing.detail.project.metrics')" :key="metric.label">
              <strong>{{ metric.value }}</strong><span>{{ metric.label }}</span>
            </article>
          </div>
        </section>

        <section class="mkt-section mkt-project-story-section">
          <div class="mkt-shell">
            <div class="mkt-detail-section-header">
              <div><span class="mkt-eyebrow">{{ t('marketing.detail.project.storyEyebrow') }}</span><h2>{{ t('marketing.detail.project.storyTitle') }}</h2></div>
            </div>
            <div class="mkt-detail-reading-layout">
              <article class="mkt-detail-body mkt-project-story-body">
                <img :src="detail.image_1" :alt="detail.project_title" @error="applyImageFallback">
                <div class="mkt-rich-content" v-html="detail.content"></div>
              </article>
              <aside class="mkt-detail-side-cta mkt-project-support-panel">
                <span>{{ t('marketing.detail.project.supportTitle') }}</span>
                <h2>{{ t('marketing.detail.project.supportTitle') }}</h2>
                <p>{{ t('marketing.detail.project.supportText') }}</p>
                <ul>
                  <li v-for="point in t('marketing.detail.project.supportPoints')" :key="point"><i class="mdi mdi-check"></i>{{ point }}</li>
                </ul>
                <a class="mkt-button mkt-button-primary" href="#detail-inquiry">{{ t('marketing.detail.common.quote') }}</a>
              </aside>
            </div>
          </div>
        </section>

        <section v-if="relatedProjects.length" class="mkt-section mkt-detail-related-section is-dark">
          <div class="mkt-shell">
            <div class="mkt-detail-section-header">
              <div><span class="mkt-eyebrow is-light">{{ t('marketing.detail.project.relatedEyebrow') }}</span><h2>{{ t('marketing.detail.project.relatedTitle') }}</h2></div>
              <a class="mkt-text-link is-light" href="/projects">{{ t('marketing.detail.common.viewAll') }}<i class="mdi mdi-arrow-right"></i></a>
            </div>
            <div class="mkt-detail-related-grid">
              <article v-for="item in relatedProjects" :key="item.id">
                <a :href="`/projects/${encodeURIComponent(item.site || item.id)}`">
                  <img :src="item.image_1" :alt="item.project_title" loading="lazy" @error="applyImageFallback">
                  <div><span>{{ t('marketing.detail.project.eyebrow') }}</span><h3>{{ item.project_title }}</h3><i class="mdi mdi-arrow-top-right"></i></div>
                </a>
              </article>
            </div>
          </div>
        </section>

        <section id="detail-inquiry" class="mkt-inquiry-section mkt-detail-inquiry-section">
          <div class="mkt-shell mkt-inquiry-layout">
            <div class="mkt-inquiry-copy">
              <span class="mkt-eyebrow is-light">{{ t('marketing.detail.project.inquiryEyebrow') }}</span>
              <h2>{{ t('marketing.detail.project.inquiryTitle') }}</h2>
              <p>{{ t('marketing.detail.project.inquiryText') }}</p>
              <ul class="mkt-check-list is-light"><li v-for="item in t('marketing.home.inquiry.benefits')" :key="item"><i class="mdi mdi-check"></i>{{ item }}</li></ul>
            </div>
            <MarketingInquiryForm :context="pageContext"/>
          </div>
        </section>
      </template>
    </main>
    <Footer/>
  </div>
</template>
